import { useEffect, useState, useMemo } from "react";
import { X, Loader2, ShoppingBag, CheckSquare, Square, Search, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { fetchTebexPackages } from "@/api";

/**
 * Modal that lists every package on the connected Tebex storefront and lets
 * the admin one-click-import them as products on the site.
 */
export default function TebexSyncModal({ open, onClose, categories = [], onImport }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState({});  // { [tebex_id]: true }
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setLoading(true);
    setSelected({});
    setSearch("");
    fetchTebexPackages()
      .then((d) => {
        setPackages(d.packages || []);
        if (!(d.packages || []).length) setError("Tebex returned 0 packages — make sure your store has published packages.");
      })
      .catch((e) => setError(e.response?.data?.detail || e.message || "Failed to load Tebex packages"))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!target && categories.length > 0) setTarget(categories[0].id);
  }, [categories, target]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter(
      (p) => (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)
    );
  }, [packages, search]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allSelected = filtered.length > 0 && filtered.every((p) => selected[p.id]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (allSelected) {
      const next = { ...selected };
      filtered.forEach((p) => { delete next[p.id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filtered.forEach((p) => { next[p.id] = true; });
      setSelected(next);
    }
  };

  const doImport = () => {
    const picks = packages.filter((p) => selected[p.id]);
    if (picks.length === 0) {
      toast.error("Select at least one package");
      return;
    }
    onImport?.(picks, target);
  };

  if (!open) return null;

  return (
    <div
      data-testid="tebex-sync-modal"
      className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-[#0a0a0a] border border-[var(--accent-dim)] w-full max-w-5xl max-h-[88vh] flex flex-col"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(var(--theme-accent-primary-rgb),0.04) 1px, transparent 1.5px)",
          backgroundSize: "16px 16px",
        }}
      >
        {/* HEADER */}
        <div className="p-5 border-b border-[var(--accent-dim)] flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="bracket">// TEBEX_STOREFRONT_SYNC</div>
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight flex items-center gap-2">
              <ShoppingBag size={20} className="text-[var(--accent)]" />
              IMPORT PACKAGES FROM TEBEX
            </h2>
          </div>
          <button
            data-testid="tebex-sync-close"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="px-5 py-3 border-b border-[var(--line)] flex items-center gap-3 flex-wrap bg-black/60">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              placeholder="filter by name or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none pl-8 pr-3 py-2 text-xs font-mono"
            />
          </div>
          <button
            onClick={toggleAll}
            disabled={filtered.length === 0}
            className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1 disabled:opacity-30"
          >
            {allSelected ? <CheckSquare size={11} /> : <Square size={11} />}
            {allSelected ? "DESELECT ALL" : "SELECT ALL"}
          </button>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            {selectedCount}/{packages.length} selected
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-[var(--accent)]">
              <Loader2 size={28} className="animate-spin" />
              <div className="font-mono text-[10px] uppercase tracking-widest">// PULLING FROM TEBEX...</div>
            </div>
          )}
          {!loading && error && (
            <div className="py-12 px-6 border border-red-500/40 bg-red-500/5 text-center font-mono text-xs text-red-400 flex flex-col items-center gap-2">
              <AlertCircle size={20} />
              <div>{error}</div>
            </div>
          )}
          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((pkg) => {
                const checked = !!selected[pkg.id];
                return (
                  <button
                    key={pkg.id}
                    data-testid={`tebex-pkg-${pkg.id}`}
                    onClick={() => toggle(pkg.id)}
                    className={`relative text-left p-3 border transition flex gap-3 ${
                      checked
                        ? "border-[var(--accent)] bg-[var(--accent)]/10"
                        : "border-[var(--line-strong)] hover:border-[var(--accent)]/50 bg-black/40"
                    }`}
                  >
                    <div className="w-16 h-16 bg-black border border-[var(--line)] flex-shrink-0 overflow-hidden">
                      {pkg.image ? (
                        <img src={pkg.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-mono text-[10px] text-[var(--muted)]">
                          //
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--accent)] truncate">
                        #{pkg.id} {pkg.category ? `· ${pkg.category}` : ""}
                      </div>
                      <div className="font-display font-bold uppercase text-sm leading-tight mt-0.5 truncate">
                        {pkg.name}
                      </div>
                      {pkg.price && (
                        <div className="font-mono text-xs text-[var(--accent)] mt-1">{pkg.price}</div>
                      )}
                    </div>
                    <div className={`flex-shrink-0 w-5 h-5 border ${checked ? "bg-[var(--accent)] border-[var(--accent)]" : "border-[var(--line-strong)]"} flex items-center justify-center`}>
                      {checked && <span className="text-black text-xs font-bold">✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {!loading && !error && filtered.length === 0 && packages.length > 0 && (
            <div className="text-center py-12 font-mono text-xs text-[var(--muted)]">// no packages match your search</div>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-5 border-t border-[var(--accent-dim)] bg-black/80 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            &gt; IMPORT INTO:
            <select
              data-testid="tebex-sync-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-1 text-xs text-white font-mono"
            >
              {categories.length === 0 && <option value="">// new category "From Tebex"</option>}
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name || "Untitled"}</option>
              ))}
              <option value="">+ new category "From Tebex"</option>
            </select>
          </label>
          <button
            data-testid="tebex-sync-import"
            onClick={doImport}
            disabled={loading || selectedCount === 0}
            className="ml-auto btn-mega !py-3 !px-5 !text-xs disabled:opacity-30 disabled:cursor-not-allowed"
          >
            IMPORT {selectedCount > 0 ? `${selectedCount} ` : ""}PACKAGE{selectedCount === 1 ? "" : "S"}
          </button>
        </div>
      </div>
    </div>
  );
}
